import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Box, Typography } from "@mui/material";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { ReactElement, useCallback } from "react";
import { Accept, FileError, useDropzone } from "react-dropzone";

import { palette } from "~frontend/theme";

export type FilePickerProps = {
  onPickFile: (file: File) => void;
  dropzoneText: string;
  accept?: Accept;
  maxFiles?: number | null;
  maxSizeMB?: number | null;
};

export default function FilePicker({
  onPickFile,
  dropzoneText,
  accept,
  maxFiles,
  maxSizeMB,
}: FilePickerProps): ReactElement {
  const snackbar = useSnackbar();

  function formatError(error: FileError): string {
    if (error.code === "file-invalid-type") {
      const acceptedExtensions = Object.values(accept || {})
        .flat()
        .join(", ");
      return `Invalid file type (must be ${acceptedExtensions})`;
    } else {
      return error.message;
    }
  }

  const validateFile = useCallback(
    (file: File) => {
      const fileSizeMB = file.size / (1024 * 1024);
      if (maxSizeMB && fileSizeMB > maxSizeMB) {
        return {
          code: "file-too-large",
          message: `File cannot be larger than ${maxSizeMB} MB`,
        };
      }
      return null;
    },
    [maxSizeMB]
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    maxFiles: maxFiles ?? undefined,
    validator: validateFile,
    onDrop: (files, rejections) => {
      if (rejections.length > 0) {
        snackbar.enqueueSnackbar(
          rejections.flatMap(r => r.errors.map(formatError)).join(". "),
          {
            variant: "error",
          }
        );
      } else if (files.length > 0) {
        onPickFile(files[0]);
      }
    },
  });

  return (
    <div className="container">
      <Box
        sx={{
          padding: "40px",
          borderWidth: "1px",
          borderRadius: "4px",
          borderColor: palette.midGray,
          borderStyle: "dashed",
          outline: "none",
          transition: "border 0.24s ease-in-out",
          textAlign: "center",
        }}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <Typography variant="body2" sx={{ marginBottom: "16px" }}>
          {dropzoneText}
        </Typography>
        <CloudUploadIcon sx={{ fontSize: "40px" }} />
      </Box>
    </div>
  );
}
